"use client";
import React, { useState, useEffect, useRef } from "react";
import "./faqSection.css";
import { downArrowIcon } from "../../../utils/icons";

const FaqItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  const renderAnswer = () => {
    // Split the answer into lines and remove any empty ones
    const lines = answer.split('\n').filter(line => line.trim() !== '');

    // If it's a single line of text, render a simple paragraph
    if (lines.length <= 1) {
      return <p>{answer}</p>;
    }

    // If it's a bulleted list
    if (lines[0].trim().startsWith('•')) {
      return (
        <ul>
          {lines.map((line, index) => (
            <li key={index}>{line.replace('•', '').trim()}</li>
          ))}
        </ul>
      );
    }

    // If it's a numbered list
    if (lines[0].trim().match(/^\d+\./)) {
      return (
        <ol>
          {lines.map((line, index) => (
            <li key={index}>{line.replace(/^\d+\.\s*/, '').trim()}</li>
          ))}
        </ol>
      );
    }

    // Fallback for multi-line text that isn't a list
    return <p style={{ whiteSpace: 'pre-line' }}>{answer}</p>;
  };

  return (
    <div className="faq__item" onClick={(e) => e.stopPropagation()}>
      <button className="faq__q" onClick={() => setIsOpen(!isOpen)}>
        {question}
        <span
          className="faq__icon"
          style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}>
          {downArrowIcon}
        </span>
      </button>
      <div className={`faq__a ${isOpen ? "open" : ""}`}>{renderAnswer()}</div>
    </div>
  );
};

const FaqSection = () => {
  const faqList = [
    // Sobre GourmetJobs
    {
      question: "¿Qué es GourmetJobs?",
      answer: "GourmetJobs es una plataforma de reclutamiento especializada en gastronomía. Conectamos restaurantes, cafeterías, bares, hoteles y negocios gastronómicos con trabajadores calificados como chefs, garzones, baristas, coperos y más.",
    },
    {
      question: "¿Quiénes pueden usar GourmetJobs?",
      answer: "• Trabajadores gastronómicos que buscan empleo\n• Empresas y locales gastronómicos que necesitan contratar\n• Hoteles, banqueterías, dark kitchens, food trucks y más",
    },
    {
      question: "¿Es gratuito registrarse?",
      answer: "• Trabajadores: Sí, siempre gratis\n• Empresas: Hay un plan gratuito limitado y planes pagados con más funcionalidades",
    },
    {
      question: "¿Dónde funciona GourmetJobs?",
      answer: "Actualmente en Chile, con expansión planificada a Latinoamérica.",
    },
    {
      question: "¿Qué diferencia a GourmetJobs de otras bolsas de empleo?",
      answer: "No somos una bolsa de empleo. Somos una plataforma de reclutamiento que ofrece:\n• Enfoque 100% gastronómico\n• Filtros y herramientas creadas para el rubro\n• Comunidad activa de empleadores y trabajadores\n• Modelo freemium accesible y flexible",
    },
    {
      question: "¿Qué ventajas tengo al contratar con GourmetJobs?",
      answer: "• Acceso a base de trabajadores especializados\n• Publicación rápida y sencilla\n• Filtros avanzados\n• Suscripciones accesibles y flexibles\n• Mayor visibilidad en la industria gastronómica",
    },
    {
      question: "¿Cuánto cuesta publicar un empleo?",
      answer: "Trabajamos con modelo de suscripción:\n• Plan gratuito: Funciones básicas y limitadas\n• Planes pagos: Mayor visibilidad y herramientas avanzadas para optimizar contrataciones",
    },
    {
      question: "¿Cómo registro mi empresa?",
      answer: "• Ingresa a www.gourmetjobs.cl\n• Haz clic en Empresas > Registrar\n• Completa la información de tu negocio\n• Verifica tu cuenta y accede a tu panel",
    },
    {
      question: "¿Cómo publico una oferta?",
      answer: "• Inicia sesión en tu panel de empresa\n• Haz clic en \"Publicar empleo\"\n• Completa los detalles del puesto\n• Activa la publicación y comienza a recibir postulaciones",
    },
    {
      question: "¿Puedo gestionar candidatos desde la plataforma?",
      answer: "Sí. Puedes revisar CVs, filtrar candidatos y contactar directamente a los seleccionados desde tu panel.",
    },
    {
      question: "¿Puedo buscar perfiles sin esperar postulaciones?",
      answer: "Sí, con planes pagos accedes a la base de trabajadores registrados y filtras por cargo, experiencia y ubicación.",
    },
    {
      question: "¿Puedo manejar varias sucursales?",
      answer: "Sí, contamos con la función Administración Multilocal para grupos gastronómicos.",
    },
    {
      question: "¿Soy trabajador, debo pagar para postular a un trabajo?",
      answer: "No. Postular es 100% gratuito.",
    },
    {
      question: "¿Cómo me registro para encontrar oportunidades?",
      answer: "Solo necesitas crear tu cuenta, completar tu perfil con experiencia, disponibilidad y ¡listo!, ya puedes empezar a postular.",
    },
    {
      question: "¿Cómo sabré si una empresa me contacta o selecciona?",
      answer: "Te llegará un correo electrónico y una notificación en la plataforma cuando una empresa se interese en tu perfil.",
    },
    {
      question: "¿Qué tipo de empleos puedo encontrar?",
      answer: "Garzones, baristas, cocineros, chefs, ayudantes de cocina, coperos, encargados de local, pasteleros y más. Hay empleos full-time, part-time, por turnos y más.",
    },
    {
      question: "¿Puedo postular a varios trabajos al mismo tiempo?",
      answer: "Sí, puedes postular a todas las ofertas que quieras y elegir la que más te acomode.",
    },
    {
      question: "¿Cómo aumento mis posibilidades de ser contratado?",
      answer: "Mantén tu perfil completo y actualizado con tu experiencia, habilidades y certificaciones. Mientras más activo y conectado estés en la plataforma, más visibilidad tendrás frente a las empresas.",
    },
  ];

  const [showAll, setShowAll] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    if (window.location.hash === "#faq" && sectionRef.current) {
      sectionRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  const visibleFaqs = showAll ? faqList : faqList.slice(0, 6);

  return (
    <section
      className="faq"
      id="faq"
      ref={sectionRef}
      data-alt-ids="faqs,preguntas-frecuentes"
    >
      <div className="faq__inner">
        <h2 className="faq__title">Preguntas Frecuentes</h2>
        <div className="faq__grid">
          {visibleFaqs.map((faqItem, index) => (
            <FaqItem
              key={index}
              question={faqItem.question}
              answer={faqItem.answer}
            />
          ))}
        </div>
        <div className="faq__more">
          <button className="faq__more-btn" onClick={() => setShowAll((v) => !v)}>
            {showAll ? "Ver menos" : "Ver todas las preguntas frecuentes"} {" "}
            <span style={{ color: "#fb5424" }}>▶</span>
          </button>
        </div>
      </div>
    </section>
  );
};

export default FaqSection;
